import React, { ReactNode, useId } from 'react';
interface FieldProps {
  label: string;
  hint?: string;
  children: (id: string) => ReactNode;
}
export function Field({
  label,
  hint,
  children
}: FieldProps) {
  const id = useId();
  return <div data-id="element-1249" data-mp="se3xnql1a7fh">
      <label htmlFor={id} className="mb-1.5 block text-sm font-bold" data-id="element-1250" data-mp="sdkbfxocowvn">
        {label}
      </label>
      {children(id)}
      {hint && <p className="mt-1 text-xs text-muted" data-id="element-1251" data-mp="xbvlo9ik59ee">{hint}</p>}
    </div>;
}
interface ToggleProps {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  hint?: string;
  disabled?: boolean;
}
export function Toggle({
  checked,
  onChange,
  label,
  hint,
  disabled
}: ToggleProps) {
  return <button type="button" role="switch" aria-checked={checked} disabled={disabled} onClick={() => onChange(!checked)} className="flex w-full items-center gap-3 rounded-2xl py-2 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent disabled:opacity-60" data-id="element-1252" data-mp="1b7zebyds99or">
      <span className="min-w-0 flex-1" data-id="element-1253" data-mp="1lvxx36pa0mcs">
        <span className="block text-sm font-bold" data-id="element-1254" data-mp="1lwzj267tyt9a">{label}</span>
        {hint && <span className="block text-xs text-muted" data-id="element-1255" data-mp="1gva78ocsabk5">{hint}</span>}
      </span>
      <span className={`relative h-7 w-12 shrink-0 rounded-full transition-colors duration-150 ${checked ? 'bg-done' : 'bg-line'}`} data-id="element-1256" data-mp="1bwz0imhsch65">
        <span className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-transform duration-150 ease-out ${checked ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-1257" data-mp="17wyi6qrkfltt" />
      </span>
    </button>;
}
interface ChipProps {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
  color?: string;
}
export function Chip({
  active,
  onClick,
  children,
  color
}: ChipProps) {
  return <button type="button" onClick={onClick} aria-pressed={active} className={`inline-flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border-[1.5px] px-3 text-sm font-bold transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent ${active ? 'border-ink bg-ink text-white' : 'border-line bg-surface text-ink hover:bg-canvas'}`} data-id="element-1258" data-mp="1qtznkcp17nya">
      {color && <span className="h-2.5 w-2.5 rounded-full" style={{
      backgroundColor: color
    }} aria-hidden="true" data-id="element-1259" data-mp="6yva8dvlp8bs" />}
      {children}
    </button>;
}
interface SegmentedProps<T extends string> {
  value: T;
  options: {
    value: T;
    label: string;
  }[];
  onChange: (v: T) => void;
  label: string;
}
export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label
}: SegmentedProps<T>) {
  return <div role="tablist" aria-label={label} className="inline-flex rounded-2xl bg-line/60 p-1" data-id="element-1260" data-mp="3k5dga2i1x6pc">
      {options.map(o => <button key={o.value} type="button" role="tab" aria-selected={value === o.value} onClick={() => onChange(o.value)} className={`h-9 whitespace-nowrap rounded-xl px-4 text-sm font-bold transition-colors duration-150 ${value === o.value ? 'bg-surface text-ink shadow-card' : 'text-muted hover:text-ink'}`} data-id="element-1261" data-mp="3p4q3wpgqyhsz">
          {o.label}
        </button>)}
    </div>;
}
export function EffectiveChoice({
  value,
  onChange
}: {
  value: 'takoj' | 'naslednji-teden';
  onChange: (v: 'takoj' | 'naslednji-teden') => void;
}) {
  return <fieldset className="rounded-2xl bg-sun-soft p-3" data-id="element-1262" data-mp="3cse775u245w6">
      <legend className="sr-only" data-id="element-1263" data-mp="3qkccf5kndhn2">Velja od</legend>
      <p className="text-sm font-bold text-sun-ink" data-id="element-1264" data-mp="3qn6uiqg6uqdb">Velja od</p>
      <div className="mt-2 flex flex-wrap gap-2" data-id="element-1265" data-mp="3miqrcwxylx1s">
        <Chip active={value === 'naslednji-teden'} onClick={() => onChange('naslednji-teden')} data-id="element-1266" data-mp="4dvv8t0zw67a">
          Naslednji teden (12. 10.)
        </Chip>
        <Chip active={value === 'takoj'} onClick={() => onChange('takoj')} data-id="element-1267" data-mp="3vli7vb5ozj0a">
          Takoj (tekoči teden)
        </Chip>
      </div>
      <p className="mt-2 text-xs text-sun-ink" data-id="element-1268" data-mp="267d4200v3d3m">Zaključeni tedni ohranijo takratne vrednosti.</p>
    </fieldset>;
}